// Settings → Business → Logo (boards LogoSettings, LogoSettingsPhone, LogoUpload, LogoStates;
// docs/plans/bill-logo.md). The logo is saved on its own, not through the form's Save bar:
// choosing a file opens a dialog that shows what will print, and Save logo writes it.
import type { SyncOp } from '@ims/core'
import type { InvoiceLineRow, InvoiceRow, TenantSettingsRow } from '@ims/data'
import { Button, ConfirmDialog, Modal, Switch, cx } from '@ims/ui'
import { ImagePlus, Trash2 } from '@ims/ui/icons'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { dataUrlKb, prepareLogo, type PreparedLogo } from '../lib/logo.ts'
import { updateOp } from '../lib/rows.ts'
import { InvoiceA4, Receipt } from '../print.tsx'
import { useCan } from '../workspace.tsx'
import { SettingsPart } from './settings-part.tsx'

type SaveFn = (ops: SyncOp[], message?: string) => Promise<boolean>

interface Chosen {
  file: File
  logo: PreparedLogo | null
  error: string | null
}

/** The file being looked at and whether Remove is asking: kept by a parent that stays mounted. */
export function useLogoChoice() {
  const [chosen, setChosen] = useState<Chosen | null>(null)
  const [removing, setRemoving] = useState(false)
  return { chosen, setChosen, removing, setRemoving }
}
export type LogoChoice = ReturnType<typeof useLogoChoice>

export function LogoPart({
  settings,
  save,
  busy,
  choice,
}: {
  settings: TenantSettingsRow
  save: SaveFn
  busy: boolean
  choice: LogoChoice
}) {
  const canEdit = useCan('admin')
  const input = useRef<HTMLInputElement>(null)
  const { chosen, setChosen, removing, setRemoving } = choice
  const pick = () => input.current?.click()
  const onFile = async (file: File | undefined) => {
    if (!file) return
    setChosen({ file, logo: null, error: null })
    try {
      setChosen({ file, logo: await prepareLogo(file), error: null })
    } catch (error) {
      setChosen({ file, logo: null, error: error instanceof Error ? error.message : String(error) })
    }
  }
  const write = (changes: Partial<TenantSettingsRow>, message: string) =>
    save([updateOp('tenant_settings', settings, changes)], message)

  return (
    <SettingsPart
      title="Logo"
      about="At the top of bills, receipts, quotations and statements, beside your name. Owners and admins change it."
    >
      {/* Its own saves: the Business form's Save bar does not see these controls. */}
      <div data-own-save className="flex flex-col gap-4 sm:col-span-2">
        <input
          ref={input}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="sr-only"
          aria-label="Logo file"
          tabIndex={-1}
          onChange={(e) => {
            void onFile(e.target.files?.[0])
            e.target.value = ''
          }}
        />
        {settings.logo ? (
          <div className="flex flex-wrap items-center gap-4">
            <LogoTile src={settings.logo} />
            {canEdit ? (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    icon={<ImagePlus className="size-4" />}
                    onClick={pick}
                  >
                    Change logo
                  </Button>
                  <Button
                    variant="ghost"
                    className="text-danger hover:text-danger"
                    icon={<Trash2 className="size-4" />}
                    onClick={() => setRemoving(true)}
                  >
                    Remove
                  </Button>
                </div>
                <p className="text-xs text-muted">
                  PNG, JPG or WebP. Blank edges are trimmed and it is fitted to 400 × 160.
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted">Only owners and admins change the logo.</p>
            )}
          </div>
        ) : canEdit ? (
          <button
            type="button"
            onClick={pick}
            className="flex h-24 items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed border-border bg-neutral-wash font-semibold text-primary hover:border-primary/40"
          >
            <ImagePlus className="size-5" /> Add your logo
          </button>
        ) : (
          <p className="text-sm text-muted">No logo yet. Owners and admins add it.</p>
        )}
        <Switch
          label="Show on receipts"
          description="Thermal printers print it in black and white. Turn it off if your printer is slow with pictures."
          checked={settings.logo_on_receipt}
          disabled={!canEdit || busy}
          onChange={(e) =>
            void write(
              { logo_on_receipt: e.target.checked },
              e.target.checked ? 'Receipts show the logo' : 'Receipts print without the logo',
            )
          }
        />
        {settings.logo && <LogoPreview settings={settings} />}
      </div>
      {chosen && (
        <LogoDialog
          chosen={chosen}
          settings={settings}
          busy={busy}
          onAnother={pick}
          onClose={() => setChosen(null)}
          onSave={async (logo) => {
            if (await write({ logo }, 'Logo saved')) setChosen(null)
          }}
        />
      )}
      <ConfirmDialog
        open={removing}
        title="Remove the logo from every bill?"
        message="Bills print with your name only, as before. Bills already printed are not touched."
        confirmLabel="Remove"
        danger
        busy={busy}
        onClose={() => setRemoving(false)}
        onConfirm={async () => {
          if (await write({ logo: null }, 'Logo removed')) setRemoving(false)
        }}
      />
    </SettingsPart>
  )
}

function LogoTile({ src, className }: { src: string; className?: string }) {
  return (
    <span
      className={cx(
        'flex h-24 w-56 items-center justify-center rounded-xl border border-border bg-white p-3',
        className,
      )}
    >
      <img src={src} alt="Your logo" className="max-h-full max-w-full object-contain" />
    </span>
  )
}

// A sample bill for the previews: what a real one looks like with this logo.
const SAMPLE_INVOICE = {
  id: '00000000-0000-4000-8000-000000000000',
  status: 'finalized',
  number: 'INV-0412',
  invoice_date: new Date().toISOString().slice(0, 10),
  due_date: null,
  customer_name: 'Walk-in customer',
  customer_pan: null,
  customer_address: null,
  subtotal_paisa: 70000,
  discount_paisa: 0,
  taxable_paisa: 70000,
  tax_paisa: 0,
  round_off_paisa: 0,
  total_paisa: 70000,
  notes: null,
  sales_order_id: null,
} as unknown as InvoiceRow
const SAMPLE_LINES = [
  ['Chicken momo', 2000, 18000],
  ['Chowmein', 1000, 22000],
  ['Lassi', 1000, 12000],
].map(
  ([description, qty, price], i) =>
    ({
      id: `sample-${i}`,
      line_no: i + 1,
      description,
      qty_milli: qty,
      unit_price_paisa: price,
      discount_paisa: 0,
      tax_rate_bp: 0,
      total_paisa: ((qty as number) / 1000) * (price as number),
      batch_id: null,
      serials: null,
    }) as unknown as InvoiceLineRow,
)

/** The A4 bill and the receipt, small, with the logo as it will print. */
export function LogoPreview({ settings }: { settings: TenantSettingsRow }) {
  const doc = { invoice: SAMPLE_INVOICE, lines: SAMPLE_LINES, settings, balancePaisa: 0 }
  return (
    <div className="flex flex-wrap items-start gap-4 rounded-xl border border-border bg-neutral-wash p-3">
      <Mini caption="On an A4 bill" width={794} scale={0.3} height={210}>
        <div style={{ padding: 40 }}>
          <InvoiceA4 {...doc} />
        </div>
      </Mini>
      {settings.logo_on_receipt && (
        <Mini caption="On a receipt, in black" width={290} scale={0.5} height={210}>
          <div style={{ padding: 8 }}>
            <Receipt {...doc} />
          </div>
        </Mini>
      )}
    </div>
  )
}

function Mini({
  caption,
  width,
  scale,
  height,
  children,
}: {
  caption: string
  width: number
  scale: number
  height: number
  children: ReactNode
}) {
  return (
    <figure className="flex flex-col gap-1.5">
      <div
        aria-hidden
        className="overflow-hidden rounded-lg bg-white shadow-card ring-1 ring-border"
        style={{ width: width * scale, height }}
      >
        <div style={{ width, transform: `scale(${scale})`, transformOrigin: '0 0' }}>
          {children}
        </div>
      </div>
      <figcaption className="text-xs text-muted">{caption}</figcaption>
    </figure>
  )
}

function LogoDialog({
  chosen,
  settings,
  busy,
  onAnother,
  onClose,
  onSave,
}: {
  chosen: { file: File; logo: PreparedLogo | null; error: string | null }
  settings: TenantSettingsRow
  busy: boolean
  onAnother: () => void
  onClose: () => void
  onSave: (logo: string) => void
}) {
  // The file as chosen, shown beside what will print.
  const original = useMemo(() => URL.createObjectURL(chosen.file), [chosen.file])
  useEffect(() => () => URL.revokeObjectURL(original), [original])
  const { logo, error } = chosen
  return (
    <Modal
      open
      onClose={onClose}
      title="Your logo"
      size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          <Button variant="ghost" icon={<ImagePlus className="size-4" />} onClick={onAnother}>
            Choose another
          </Button>
          <span className="ml-auto flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!logo} loading={busy} onClick={() => logo && onSave(logo.dataUrl)}>
              Save logo
            </Button>
          </span>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="-mt-1 text-sm text-muted">
          This is how it will print. Saved with your business, so every device prints it, offline
          too.
        </p>
        {error ? (
          <p role="alert" className="rounded-xl bg-danger-light px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <figure className="flex flex-col gap-1.5">
              <div className="flex h-36 items-center justify-center rounded-xl border border-border bg-neutral-wash p-3">
                {original && (
                  <img
                    src={original}
                    alt="The file you chose"
                    className="max-h-full max-w-full object-contain"
                  />
                )}
              </div>
              <figcaption className="text-xs text-muted">
                What you chose: {chosen.file.name} ·{' '}
                {Math.max(1, Math.round(chosen.file.size / 1024))} KB
              </figcaption>
            </figure>
            <figure className="flex flex-col gap-1.5">
              <div className="flex h-36 items-center justify-center rounded-xl border border-border bg-white p-3">
                {logo ? (
                  <img
                    src={logo.dataUrl}
                    alt="Your logo as it prints"
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <span className="text-sm text-muted">Making it ready…</span>
                )}
              </div>
              <figcaption className="text-xs text-muted">
                {logo
                  ? `What prints: edges trimmed, on white, ${logo.width} × ${logo.height} · ${dataUrlKb(logo.dataUrl)} KB`
                  : 'What prints'}
              </figcaption>
            </figure>
          </div>
        )}
        {logo && (
          <>
            <LogoPreview settings={{ ...settings, logo: logo.dataUrl }} />
            <p className="text-xs text-muted">
              Check that it reads at receipt size. A thin or light logo can vanish in black and
              white: a bolder version works better there.
            </p>
          </>
        )}
      </div>
    </Modal>
  )
}
